import React, { useState, useEffect, useCallback } from 'react';
import {
  Plug, Plus, Settings, Trash2, RefreshCw, CheckCircle2, AlertCircle,
  ExternalLink, Key, Database, Shield, CreditCard, Users, FileText,
  Loader2, ChevronRight, Link2, Webhook, Clock, BookOpen, Copy,
  Building2, Globe, Zap, TestTube
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Integration type icons and colors
const INTEGRATION_TYPE_CONFIG = {
  crm: {
    icon: Users,
    label: 'CRM Systems',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
  sanctions_screening: {
    icon: Shield,
    label: 'Sanctions/PEP Screening',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30'
  },
  kyc_verification: {
    icon: FileText,
    label: 'KYC Verification',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30'
  },
  transaction_monitoring: {
    icon: CreditCard,
    label: 'Transaction Monitoring',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  }
};

export default function IntegrationHubPage() {
  const [providers, setProviders] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [documentation, setDocumentation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('configurations');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [newConfig, setNewConfig] = useState({
    provider_id: '',
    credentials: {},
    settings: {}
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [providersRes, configsRes, docsRes] = await Promise.all([
        fetch(`${API_URL}/api/integrations/providers`, { headers }),
        fetch(`${API_URL}/api/integrations/configurations`, { headers }),
        fetch(`${API_URL}/api/integrations/documentation`, { headers })
      ]);

      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(data.providers || []);
      }

      if (configsRes.ok) {
        const data = await configsRes.json();
        setConfigurations(data.configurations || []);
      }

      if (docsRes.ok) {
        const data = await docsRes.json();
        setDocumentation(data);
      }
    } catch {
      // Failed to load integration data
      toast.error('Failed to load integration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProviderSelect = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    setSelectedProvider(provider);
    setNewConfig({
      provider_id: providerId,
      credentials: {},
      settings: {}
    });
  };

  const handleCreateConfiguration = async () => {
    if (!newConfig.provider_id) {
      toast.error('Please select a provider');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/configurations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newConfig)
      });

      if (response.ok) {
        toast.success('Integration configured successfully');
        setShowAddModal(false);
        setSelectedProvider(null);
        setNewConfig({ provider_id: '', credentials: {}, settings: {} });
        fetchData();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create configuration');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (configId) => {
    setTesting(configId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/integrations/configurations/${configId}/test`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleDeleteConfiguration = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/integrations/configurations/${configId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Integration deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Group providers by type
  const providersByType = providers.reduce((acc, provider) => {
    const type = provider.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(provider);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integration-hub-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Plug className="w-7 h-7 text-cyan-400" />
            Integration Hub
          </h1>
          <p className="text-slate-400 mt-1">
            Connect your Regtech tools and CRM systems
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowDocsModal(true)}
            className="border-slate-600"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            API Docs
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-cyan-500 to-blue-500"
            data-testid="add-integration-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Integration
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="configurations">
            <Settings className="w-4 h-4 mr-2" />
            My Integrations
          </TabsTrigger>
          <TabsTrigger value="providers">
            <Globe className="w-4 h-4 mr-2" />
            Available Providers
          </TabsTrigger>
          <TabsTrigger value="webhooks">
            <Webhook className="w-4 h-4 mr-2" />
            Webhooks
          </TabsTrigger>
        </TabsList>

        {/* My Integrations Tab */}
        <TabsContent value="configurations" className="mt-6">
          {configurations.length === 0 ? (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-12 text-center">
              <Plug className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Integrations Configured</h3>
              <p className="text-slate-400 mb-6">
                Connect your CRM, screening tools, and other Regtech systems
              </p>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Integration
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {configurations.map((config) => {
                const typeConfig = INTEGRATION_TYPE_CONFIG[config.integration_type] || {};
                const TypeIcon = typeConfig.icon || Plug;
                
                return (
                  <div
                    key={config.id}
                    className={`bg-slate-800/50 border rounded-xl p-5 ${typeConfig.borderColor || 'border-slate-700/50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${typeConfig.bgColor || 'bg-slate-700'}`}>
                          <TypeIcon className={`w-6 h-6 ${typeConfig.color || 'text-slate-400'}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{config.provider_name}</h3>
                          <p className="text-sm text-slate-400">
                            {typeConfig.label || config.integration_type}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Status */}
                        <div className="flex items-center gap-2">
                          {config.is_active ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-500/10 text-slate-400 rounded-lg text-xs">
                              <AlertCircle className="w-3.5 h-3.5" />
                              Inactive
                            </span>
                          )}
                        </div>

                        {/* Last Sync */}
                        {config.last_sync && (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {new Date(config.last_sync).toLocaleString()}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTestConnection(config.id)}
                            disabled={testing === config.id}
                            className="text-cyan-400 hover:text-cyan-300"
                          >
                            {testing === config.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <TestTube className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteConfiguration(config.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Webhook URL */}
                    {config.webhook_url && (
                      <div className="mt-4 pt-4 border-t border-slate-700/50">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-slate-400">
                            <span className="font-medium">Webhook URL:</span>{' '}
                            <code className="bg-slate-900/50 px-2 py-1 rounded">
                              {API_URL}{config.webhook_url}
                            </code>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`${API_URL}${config.webhook_url}`)}
                            className="text-slate-400"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Available Providers Tab */}
        <TabsContent value="providers" className="mt-6">
          <div className="space-y-6">
            {Object.entries(providersByType).map(([type, typeProviders]) => {
              const typeConfig = INTEGRATION_TYPE_CONFIG[type] || {};
              const TypeIcon = typeConfig.icon || Plug;
              
              return (
                <div key={type}>
                  <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${typeConfig.color || 'text-slate-400'}`}>
                    <TypeIcon className="w-4 h-4" />
                    {typeConfig.label || type}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {typeProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-slate-600 transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="text-white font-medium">{provider.name}</h4>
                          {provider.docs_url && (
                            <a
                              href={provider.docs_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-cyan-400"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        
                        <div className="text-xs text-slate-500 mb-3">
                          <span className="inline-flex items-center gap-1 bg-slate-700/50 px-2 py-0.5 rounded">
                            <Key className="w-3 h-3" />
                            {provider.auth_type}
                          </span>
                        </div>

                        <div className="text-xs text-slate-400 mb-4">
                          Required: {provider.required_fields?.join(', ') || 'None'}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleProviderSelect(provider.id);
                            setShowAddModal(true);
                          }}
                          className="w-full border-slate-600 text-sm"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1.5" />
                          Configure
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="mt-6">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Webhook className="w-5 h-5 text-cyan-400" />
              Webhook Events
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Inbound Events */}
              <div>
                <h4 className="text-sm font-medium text-emerald-400 mb-3">Inbound Events (Receive)</h4>
                <div className="space-y-2">
                  {documentation?.webhook_events?.inbound?.map((event) => (
                    <div key={event.event} className="bg-slate-900/50 rounded-lg p-3">
                      <code className="text-xs text-cyan-400">{event.event}</code>
                      <p className="text-xs text-slate-400 mt-1">{event.description}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Outbound Events */}
              <div>
                <h4 className="text-sm font-medium text-amber-400 mb-3">Outbound Events (Send)</h4>
                <div className="space-y-2">
                  {documentation?.webhook_events?.outbound?.map((event) => (
                    <div key={event.event} className="bg-slate-900/50 rounded-lg p-3">
                      <code className="text-xs text-cyan-400">{event.event}</code>
                      <p className="text-xs text-slate-400 mt-1">{event.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Integration Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plug className="w-5 h-5 text-cyan-400" />
              Configure Integration
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Connect an external system to Navigate Aura
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Provider Selection */}
            <div>
              <Label className="text-slate-300">Provider</Label>
              <Select
                value={newConfig.provider_id}
                onValueChange={handleProviderSelect}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600 mt-1.5">
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(providersByType).map(([type, typeProviders]) => (
                    <React.Fragment key={type}>
                      <div className="px-2 py-1.5 text-xs text-slate-500 font-medium">
                        {INTEGRATION_TYPE_CONFIG[type]?.label || type}
                      </div>
                      {typeProviders.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Credentials Form */}
            {selectedProvider && (
              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-white mb-3">
                    {selectedProvider.name} Credentials
                  </h4>
                  
                  {selectedProvider.required_fields?.map((field) => (
                    <div key={field} className="mb-3">
                      <Label className="text-slate-300 text-sm capitalize">
                        {field.replace(/_/g, ' ')} *
                      </Label>
                      <Input
                        type={field.includes('secret') || field.includes('password') ? 'password' : 'text'}
                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                        value={newConfig.credentials[field] || ''}
                        onChange={(e) => setNewConfig(prev => ({
                          ...prev,
                          credentials: { ...prev.credentials, [field]: e.target.value }
                        }))}
                        className="bg-slate-900/50 border-slate-600 mt-1"
                      />
                    </div>
                  ))}

                  {selectedProvider.optional_fields?.map((field) => (
                    <div key={field} className="mb-3">
                      <Label className="text-slate-400 text-sm capitalize">
                        {field.replace(/_/g, ' ')} (optional)
                      </Label>
                      <Input
                        type="text"
                        placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                        value={newConfig.credentials[field] || ''}
                        onChange={(e) => setNewConfig(prev => ({
                          ...prev,
                          credentials: { ...prev.credentials, [field]: e.target.value }
                        }))}
                        className="bg-slate-900/50 border-slate-600 mt-1"
                      />
                    </div>
                  ))}
                </div>

                {selectedProvider.docs_url && (
                  <a
                    href={selectedProvider.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View {selectedProvider.name} Documentation
                  </a>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setSelectedProvider(null);
              }}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateConfiguration}
              disabled={saving || !selectedProvider}
              className="bg-gradient-to-r from-cyan-500 to-blue-500"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Configure Integration
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Documentation Modal */}
      <Dialog open={showDocsModal} onOpenChange={setShowDocsModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Integration API Documentation
            </DialogTitle>
          </DialogHeader>

          {documentation && (
            <div className="py-4 space-y-6">
              {/* Base URL */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Base URL</h4>
                <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3">
                  <code className="text-cyan-400 flex-1">{documentation.base_url}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(documentation.base_url)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Authentication */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Authentication</h4>
                <div className="bg-slate-800 rounded-lg p-3">
                  <p className="text-sm text-slate-400">{documentation.authentication?.description}</p>
                  <code className="text-xs text-cyan-400 mt-2 block">
                    {documentation.authentication?.example}
                  </code>
                </div>
              </div>

              {/* Endpoints */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">API Endpoints</h4>
                <Accordion type="single" collapsible className="space-y-2">
                  {Object.entries(documentation.endpoints || {}).map(([category, endpoints]) => (
                    <AccordionItem key={category} value={category} className="border-slate-700">
                      <AccordionTrigger className="text-white capitalize hover:no-underline">
                        {category.replace(/_/g, ' ')}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 pt-2">
                          {Object.entries(endpoints).map(([endpoint, description]) => (
                            <div key={endpoint} className="bg-slate-800/50 rounded p-2">
                              <code className="text-xs text-cyan-400">{endpoint}</code>
                              <p className="text-xs text-slate-400 mt-1">{String(description)}</p>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Supported Providers */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Supported Providers</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(documentation.supported_providers || {}).map(([type, providers]) => (
                    <div key={type} className="bg-slate-800/50 rounded-lg p-3">
                      <h5 className="text-xs font-medium text-cyan-400 mb-2 capitalize">
                        {type.replace(/_/g, ' ')}
                      </h5>
                      <div className="flex flex-wrap gap-1">
                        {providers.map((provider) => (
                          <span key={provider} className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                            {provider}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocsModal(false)} className="border-slate-600">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
